// src/components/pages/benefaction/BenefactorLogin.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import axios from "axios";
import { isAuthenticated, setAuthToken } from "../../../services/authService";
import { initiateEmployeeLoginOtp, verifyEmployeeLoginOtp } from "../../../services/employeeService";
import { API_BASE_URL, RECAPTCHA_SITE_KEY } from "../../../config";
import { getDonationLandById } from "../../../services/donationsService";
import { LandDetail } from "../../../types/donations";
import LandInfoPanel from "./Request/LandInfoPanel";
import { buildLandInfoRows } from "../../../utils/landInfoRows";

// --- helpers ---------------------------------------------------------------
const RESEND_COOLDOWN_S = 60;

function translateBackendErrorMessage(raw?: string | null): string | null {
  const s = (raw || "").toString().trim();
  if (!s) return null;
  const l = s.toLowerCase();

  // SMS provider failures
  if (l.includes("sms_send_failed") && l.includes("ip") && l.includes("not allowed")) {
    return "فشل الاتصال بالخادم. يرجى التحقق من الاتصال والمحاولة مرة أخرى";
  }
  if (l.includes("sms_send_failed") || l.includes("provider error")) {
    return "فشل الاتصال بالخادم. يرجى التحقق من الاتصال والمحاولة مرة أخرى";
  }

  // Cooldown / rate limit
  if (l.includes("cooldown") || l.includes("rate_limited") || l.includes("rate limited") || l.includes("too many")) {
    // Do not show a dedicated "wait 60s" message (per product requirement)
    return "فشل إرسال رمز التحقق. يرجى المحاولة مرة أخرى";
  }

  // Captcha
  if (l.includes("captcha") || l.includes("recaptcha")) {
    return "يرجى التحقق من reCAPTCHA ثم المحاولة مرة أخرى.";
  }

  // Invalid credentials
  if (
    l.includes("invalid_credentials") ||
    l.includes("bad_credentials") ||
    l.includes("wrong_password") ||
    l.includes("invalid credentials") ||
    l.includes("bad credentials") ||
    l.includes("wrong password") ||
    l.includes("invalid password") ||
    l.includes("اسم المستخدم أو كلمة المرور") ||
    l.includes("بيانات الدخول")
  ) {
    return "رقم الهوية أو كلمة المرور غير صحيحة";
  }

  // No account
  if (
    l.includes("user_not_found") ||
    l.includes("not registered") ||
    l.includes("not_registered") ||
    l.includes("no account") ||
    l.includes("لا يوجد حساب") ||
    l.includes("غير مسجل")
  ) {
    return "ليس لديك حساب في النظام";
  }

  return null;
}

// In development, prefer CRA proxy (/api/*) to avoid CORS/SSL issues.
// In production builds, use absolute API_BASE_URL.
const apiBase = () => (process.env.NODE_ENV === "development" ? "" : API_BASE_URL);

type PwStrengthLevel = "empty" | "weak" | "medium" | "strong";
function getPwStrength(pw: string): { level: PwStrengthLevel; label: string; score: number } {
  const p = (pw || "").trim();
  if (!p) return { level: "empty", label: "", score: 0 };

  const hasLetter = /[A-Za-z\u0600-\u06FF]/.test(p);
  const hasNumber = /\d/.test(p);
  const hasSymbol = /[^A-Za-z\u0600-\u06FF\d]/.test(p);

  let score = 0;
  if (p.length >= 8) score++;
  if (p.length >= 12) score++;
  if (hasLetter) score++;
  if (hasNumber) score++;
  if (hasSymbol) score++;

  if (score <= 2) return { level: "weak", label: "ضعيفة", score };
  if (score <= 4) return { level: "medium", label: "متوسطة", score };
  return { level: "strong", label: "قوية", score };
}

/**
 * Safely decode a base64url string.
 */
function b64urlDecode(s: string) {
  try {
    const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
    const normalized = s.replace(/-/g, "+").replace(/_/g, "/") + pad;
    return atob(normalized);
  } catch {
    return null;
  }
}

/**
 * Resolve a continue token into { path, state }.
 * Supports:
 *   1) base64url(JSON.stringify({ path, state }))
 *   2) raw URL-encoded path (e.g. "%2Fbenefaction%2Flands%2F1089")
 *   3) plain path ("/benefaction/lands/1089")
 * Returns undefined if invalid or unsafe.
 */
function resolveContinueToken(token?: string): { path: string; state?: any } | undefined {
  if (!token) {
    console.log("[resolveContinueToken] No token provided");
    return undefined;
  }

  console.log("[resolveContinueToken] Resolving token:", token);

  // 1) Try base64url JSON
  const decoded = b64urlDecode(token);
  if (decoded) {
    try {
      const obj = JSON.parse(decoded);
      if (obj && typeof obj.path === "string") {
        const safePath = sanitizeInternalPath(obj.path);
        if (safePath) {
          console.log("[resolveContinueToken] Resolved from base64url JSON:", safePath);
          return { path: safePath, state: obj.state };
        }
      }
    } catch {
      // not JSON → fall through
    }
  }

  // 2) Try URL-decoding a raw path
  try {
    const maybePath = decodeURIComponent(token);
    console.log("[resolveContinueToken] URL-decoded path:", maybePath);
    const safePath = sanitizeInternalPath(maybePath);
    if (safePath) {
      console.log("[resolveContinueToken] Resolved from URL-decoded path:", safePath);
      return { path: safePath };
    } else {
      console.warn("[resolveContinueToken] URL-decoded path failed sanitization:", maybePath);
    }
  } catch (e) {
    console.warn("[resolveContinueToken] URL-decoding failed:", e);
    // not URL-encoded → fall through
  }

  // 3) Treat token itself as a path
  const safePath = sanitizeInternalPath(token);
  if (safePath) {
    console.log("[resolveContinueToken] Resolved from raw token:", safePath);
    return { path: safePath };
  } else {
    console.warn("[resolveContinueToken] Raw token failed sanitization:", token);
  }

  console.error("[resolveContinueToken] Failed to resolve token:", token);
  return undefined;
}

/**
 * Ensure the path is an internal route (prevents open redirects).
 * Adjust the prefix list if you have more allowed areas.
 */
function sanitizeInternalPath(p: string): string | null {
  if (typeof p !== "string") {
    console.warn("[sanitizeInternalPath] Not a string:", typeof p);
    return null;
  }
  if (!p.startsWith("/")) {
    console.warn("[sanitizeInternalPath] Path doesn't start with /:", p);
    return null;
  }

  const allowedPrefixes = [
    "/benefaction/",
    "/services",
    "/profile",
    "/login",
    "/resident-signup",
    "/mosque/",
    "/mosques/",
    "/my-dashboard",
    "/my-requests",
  ];

  const matched = allowedPrefixes.some((pref) => p.startsWith(pref));
  if (matched) {
    console.log("[sanitizeInternalPath] Path is safe:", p);
    return p;
  }
  console.warn("[sanitizeInternalPath] Path not in allowed prefixes:", p, "Allowed:", allowedPrefixes);
  return null;
}

// --- component -------------------------------------------------------------
export default function BenefactorLogin() {
  const dir = "rtl";

  const navigate = useNavigate();
  const location = useLocation();

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const forgotRecaptchaRef = useRef<ReCAPTCHA>(null);
  const otpRef = useRef<HTMLInputElement>(null);
  const nationalIdInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const hasNavigatedRef = useRef(false);
  const safetyTimeoutRef = useRef<number | null>(null);
  const lastFpResetPasswordRef = useRef<string | null>(null);

  // carry through "?c=<continue>" if present
  const continueToken = useMemo(() => {
    try {
      const token = new URLSearchParams(location.search).get("c");
      console.log("[BenefactorLogin] Extracted continue token from URL:", {
        search: location.search,
        token,
      });
      return token ?? undefined;
    } catch (e) {
      console.error("[BenefactorLogin] Failed to extract continue token:", e);
      return undefined;
    }
  }, [location.search]);

  // Signup/Register must open "/resident-signup" (not "/benefaction/signup").
  const signUpPath = useMemo(() => {
    const base = "/resident-signup";
    return continueToken ? `${base}?c=${encodeURIComponent(continueToken)}` : base;
  }, [continueToken]);

  const continueTarget = useMemo(() => resolveContinueToken(continueToken), [continueToken]);

  const returnUrlSafe = useMemo(() => {
    try {
      const url = new URL(window.location.href);
      const returnUrl = url.searchParams.get("returnUrl");
      if (!returnUrl) return null;
      return sanitizeInternalPath(decodeURIComponent(returnUrl));
    } catch {
      return null;
    }
  }, [location.search]);

  const landIdFromContinue = useMemo(() => {
    const path = continueTarget?.path || "";
    const match = /^\/benefaction\/request\/(\d+)/.exec(path);
    const id = match ? Number(match[1]) : NaN;
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [continueTarget]);

  const [landPreview, setLandPreview] = useState<LandDetail | null>(null);
  const [landPreviewLoading, setLandPreviewLoading] = useState(false);
  const [landPreviewError, setLandPreviewError] = useState<string | null>(null);

  // top-level view toggle (login vs forgot password)
  const [view, setView] = useState<"login" | "forgot">("login");

  // state
  const [nationalId, setNationalId] = useState("");
  const [password, setPassword] = useState("");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState(""); // OTP code
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const [step, setStep] = useState<"credentials" | "otp-sent" | "verifying">("credentials");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [showNoAccount, setShowNoAccount] = useState(false);

  // ---- forgot password flow state ----------------------------------------
  const [fpNationalId, setFpNationalId] = useState("");
  const [fpLast4, setFpLast4] = useState("");
  const [fpChallengeId, setFpChallengeId] = useState<string | null>(null);
  const [fpOtp, setFpOtp] = useState("");
  const [fpNewPassword, setFpNewPassword] = useState("");
  const [fpConfirmPassword, setFpConfirmPassword] = useState("");
  const [fpStep, setFpStep] = useState<"start" | "otp" | "reset" | "resetting">("start");
  const [fpLoading, setFpLoading] = useState(false);
  const [fpCooldown, setFpCooldown] = useState(0);
  const [fpError, setFpError] = useState<string | null>(null);
  const [fpInfo, setFpInfo] = useState<string | null>(null);
  const [fpCaptchaToken, setFpCaptchaToken] = useState<string | null>(null);

  // already logged in? -> HONOR continue token if present
  useEffect(() => {
    if (!isAuthenticated()) return;
    if (hasNavigatedRef.current) return;

    hasNavigatedRef.current = true;
    if (continueTarget?.path) {
      navigate(continueTarget.path, { replace: true, state: continueTarget.state });
    } else if (returnUrlSafe) {
      navigate(returnUrlSafe, { replace: true });
    } else {
      navigate("/my-dashboard", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [continueTarget, returnUrlSafe]);

  useEffect(() => {
    let cancelled = false;
    if (!landIdFromContinue) {
      setLandPreview(null);
      setLandPreviewLoading(false);
      setLandPreviewError(null);
      return;
    }

    (async () => {
      try {
        setLandPreviewLoading(true);
        setLandPreviewError(null);
        const data = await getDonationLandById(landIdFromContinue);
        if (!cancelled) {
          setLandPreview(data ?? null);
          if (!data) setLandPreviewError("تعذر تحميل بيانات الأرض");
        }
      } catch (e: any) {
        if (!cancelled) {
          console.error("Failed to load land preview:", e);
          setLandPreview(null);
          setLandPreviewError("تعذر تحميل بيانات الأرض");
        }
      } finally {
        if (!cancelled) setLandPreviewLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [landIdFromContinue]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  useEffect(() => {
    if (fpCooldown <= 0) return;
    const id = setInterval(() => setFpCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [fpCooldown]);

  useEffect(() => {
    if (step === "otp-sent" && cooldown === 0) {
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    }
  }, [step, cooldown]);

  function isNoAccountError(e: any): boolean {
    const clientMsg = (e?.message || e?.toString() || "").toLowerCase();
    const serverMsg = String(e?.response?.data?.message ?? e?.response?.data?.Message ?? "").toLowerCase();
    // Some APIs wrap messages inside nested envelopes
    const nestedMsg = String(e?.response?.data?.result?.body?.message ?? e?.response?.data?.body?.message ?? "").toLowerCase();
    const msg = [clientMsg, serverMsg, nestedMsg].filter(Boolean).join(" | ");
    return (
      msg.includes("not found") ||
      msg.includes("not_registered") ||
      msg.includes("no account") ||
      msg.includes("لا يوجد حساب") ||
      msg.includes("غير مسجل") ||
      msg.includes("user_not_found") ||
      msg.includes("email_not_registered") ||
      msg.includes("email_not_found") ||
      e?.response?.status === 404
    );
  }

  // ---- forgot password helpers -------------------------------------------
  function extractServerMessage(err: any): string {
    let data = err?.response?.data;
    if (typeof data === "string") {
      const s = data.trim();
      if ((s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"))) {
        try {
          data = JSON.parse(s);
        } catch {
          // ignore
        }
      }
    }

    // ASP.NET ValidationProblemDetails: { title, status, errors: { field: [msg] } }
    const errorsObj = data?.errors;
    if (errorsObj && typeof errorsObj === "object") {
      try {
        const firstKey = Object.keys(errorsObj)[0];
        const v = (errorsObj as any)[firstKey];
        const firstMsg = Array.isArray(v) ? v[0] : v;
        const msg = firstMsg ? String(firstMsg) : "";
        if (msg.trim()) return msg.trim();
      } catch {
        // ignore
      }
    }

    const msg =
      data?.message ??
      data?.Message ??
      data?.error ??
      data?.Error ??
      data?.code ??
      data?.Code ??
      err?.message ??
      err?.toString?.() ??
      "";
    return typeof msg === "string" ? msg : String(msg ?? "");
  }

  async function forgotStart() {
    setFpError(null);
    setFpInfo(null);

    const nid = fpNationalId.trim();
    const last4 = fpLast4.trim();
    const captcha = (fpCaptchaToken || "").trim();

    if (!nid || nid.length < 6) {
      setFpError("يرجى إدخال رقم هوية صحيح");
      return;
    }
    if (!/^\d{4}$/.test(last4)) {
      setFpError("يرجى إدخال آخر 4 أرقام من رقم الجوال بشكل صحيح");
      return;
    }
    // If reCAPTCHA is shown, require it before sending.
    if (RECAPTCHA_SITE_KEY && !captcha) {
      setFpError("يرجى التحقق من reCAPTCHA");
      return;
    }

    setFpLoading(true);
    try {
      const url = `${apiBase()}/api/auth/forgot-password/start`;
      // Send a "superset" body once (extra fields are ignored by ASP.NET binder),
      // so we don't get stuck on DTO field name mismatches.
      const supersetBody: Record<string, any> = {
        // identifier variants
        identifier: nid,
        nationalId: nid,
        identificationId: nid,
        // last4 variants
        last4MobileDigits: last4,
        last4Mobile: last4,
        mobileLast4: last4,
        last4: last4,
        // captcha variants
        ...(captcha ? { captchaToken: captcha, recaptchaToken: captcha } : {}),
      };

      const payloads: Array<Record<string, any>> = [
        supersetBody,
        // minimal fallbacks
        { identifier: nid, last4MobileDigits: last4, ...(captcha ? { captchaToken: captcha } : {}) },
        { identifier: nid, last4Mobile: last4, ...(captcha ? { captchaToken: captcha } : {}) },
      ];

      let data: any = null;
      let lastErr: any = null;
      for (const body of payloads) {
        try {
          const res = await axios.post(url, body, { headers: { Accept: "application/json" } });
          data = res.data;
          break;
        } catch (e: any) {
          lastErr = e;
          const status = e?.response?.status ?? 0;
          // Keep trying payload variants for common "bad request / validation" statuses.
          // Stop on rate-limit or unexpected statuses.
          if (status === 429) break;
          if (status !== 400 && status !== 422) break;
        }
      }
      if (!data) throw lastErr || new Error("No response");

      const ch = data?.challengeId ?? data?.ChallengeId ?? data?.challenge_id ?? data?.id ?? data?.Id ?? null;
      if (!ch) throw new Error("Missing challengeId from server");

      setFpChallengeId(String(ch));
      setFpStep("otp");
      setFpCooldown(RESEND_COOLDOWN_S);
      setFpOtp("");
      lastFpResetPasswordRef.current = null;

      const masked = data?.maskedMobile ?? data?.MaskedMobile ?? data?.masked_mobile ?? data?.mobileMasked ?? null;
      const msg = data?.message ?? data?.Message ?? null;
      const msgLower = typeof msg === "string" ? msg.toLowerCase() : "";
      const shouldIgnoreMsg = msgLower.includes("cooldown") || msgLower.includes("request_invalid") || msgLower.includes("invalid_request");
      const maskedLast4 = masked ? String(masked).replace(/\D/g, "").slice(-4) : "";
      const ending = maskedLast4 || last4;
      const fallbackInfo = ending ? `تم إرسال رمز التحقق على الرقم المنتهي بـ ${ending}` : "تم إرسال رمز التحقق إلى رقم الجوال المرتبط بالحساب";
      setFpInfo(!shouldIgnoreMsg && msg ? String(msg) : fallbackInfo);

      setFpCaptchaToken(null);
      forgotRecaptchaRef.current?.reset?.();
      setTimeout(() => otpRef.current?.focus(), 60);
    } catch (e: any) {
      console.error("Forgot start error:", e);
      const m = extractServerMessage(e);
      const mLower = (m || "").toLowerCase();
      const status = e?.response?.status ?? 0;
      // Never show technical codes to the user (request_invalid, invalid_request, cooldown).
      if (mLower.includes("cooldown") || status === 429 || mLower.includes("rate_limited")) {
        setFpError("تم إرسال رمز التحقق بالفعل. يرجى الانتظار قليلاً ثم إعادة المحاولة.");
      } else if (mLower.includes("request_invalid") || mLower.includes("invalid_request")) {
        setFpError("تعذر إرسال رمز التحقق. يرجى التحقق من reCAPTCHA ثم المحاولة مرة أخرى.");
      } else {
        setFpError(m ? (m.length > 160 ? "تعذر إرسال رمز التحقق. يرجى المحاولة مرة أخرى" : m) : "تعذر إرسال رمز التحقق");
      }
      forgotRecaptchaRef.current?.reset?.();
      setFpCaptchaToken(null);
    } finally {
      setFpLoading(false);
    }
  }

  async function forgotVerify() {
    setFpError(null);
    setFpInfo(null);

    const ch = (fpChallengeId || "").trim();
    const otp = fpOtp.trim();
    if (!ch) {
      setFpError("تعذر متابعة التحقق. يرجى إعادة إرسال رمز التحقق.");
      return;
    }
    if (!otp || !/^\d{1,6}$/.test(otp)) {
      setFpError("يرجى إدخال رمز التحقق)");
      return;
    }

    setFpLoading(true);
    try {
      const url = `${apiBase()}/api/auth/forgot-password/verify`;
      const payloads: Array<Record<string, any>> = [
        { challengeId: ch, otpCode: otp },
        { challengeId: ch, code: otp },
        { ChallengeId: ch, OtpCode: otp },
      ];

      let ok = false;
      let lastErr: any = null;
      for (const body of payloads) {
        try {
          await axios.post(url, body, { headers: { Accept: "application/json" } });
          ok = true;
          break;
        } catch (e: any) {
          lastErr = e;
          if ((e?.response?.status ?? 0) !== 400) break;
        }
      }
      if (!ok) throw lastErr || new Error("Verify failed");

      setFpStep("reset");
      setFpInfo("تم التحقق من رمز التحقق. يمكنك الآن إعادة تعيين كلمة المرور.");
      lastFpResetPasswordRef.current = null;
    } catch (e: any) {
      console.error("Forgot verify error:", e);
      const m = extractServerMessage(e);
      setFpError(m ? (m.length > 160 ? "فشل التحقق. يرجى المحاولة مرة أخرى" : m) : "فشل التحقق. يرجى المحاولة مرة أخرى");
    } finally {
      setFpLoading(false);
    }
  }

  async function forgotReset() {
    setFpError(null);
    setFpInfo(null);

    const ch = (fpChallengeId || "").trim();
    const p1 = fpNewPassword.trim();
    const p2 = fpConfirmPassword.trim();
    if (!ch) {
      setFpError("تعذر متابعة إعادة التعيين. يرجى إعادة المحاولة.");
      return;
    }
    if (!p1) {
      setFpError("يرجى إدخال كلمة المرور الجديدة");
      return;
    }
    if (lastFpResetPasswordRef.current && lastFpResetPasswordRef.current === p1) {
      setFpError("كلمة المرور الجديدة هي نفس الرمز السابق الذي أدخلته. يرجى اختيار كلمة مرور مختلفة.");
      return;
    }

    const strength = getPwStrength(p1);
    if (strength.level === "weak") {
      setFpError("كلمة المرور غير آمنة. يرجى اختيار كلمة مرور أقوى (8 أحرف على الأقل وتحتوي على حروف وأرقام).");
      return;
    }
    if (p1 !== p2) {
      setFpError("كلمة المرور غير مطابقة");
      return;
    }

    setFpLoading(true);
    setFpStep("resetting");
    try {
      lastFpResetPasswordRef.current = p1;
      const url = `${apiBase()}/api/auth/forgot-password/reset`;
      const payloads: Array<Record<string, any>> = [
        // Most likely DTO: NewPassword + ConfirmPassword
        { challengeId: ch, newPassword: p1, confirmPassword: p2 },
        { ChallengeId: ch, NewPassword: p1, ConfirmPassword: p2 },
        // Fallback variants (some APIs use "password" naming)
        { challengeId: ch, password: p1, confirmPassword: p2 },
        { ChallengeId: ch, Password: p1, ConfirmPassword: p2 },
      ];

      let ok = false;
      let lastErr: any = null;
      for (const body of payloads) {
        try {
          await axios.post(url, body, { headers: { Accept: "application/json" } });
          ok = true;
          break;
        } catch (e: any) {
          lastErr = e;
          if ((e?.response?.status ?? 0) !== 400) break;
        }
      }
      if (!ok) throw lastErr || new Error("Reset failed");

      // success -> go back to login
      setView("login");

      // reset forgot state
      setFpStep("start");
      setFpChallengeId(null);
      setFpOtp("");
      setFpNewPassword("");
      setFpConfirmPassword("");
      setFpCooldown(0);
      setFpCaptchaToken(null);
      forgotRecaptchaRef.current?.reset?.();
      lastFpResetPasswordRef.current = null;

      setError(null);
      setInfo("تمت إعادة تعيين كلمة المرور بنجاح. يمكنك تسجيل الدخول الآن.");
    } catch (e: any) {
      console.error("Forgot reset error:", e);
      const m = extractServerMessage(e);
      setFpError(m ? (m.length > 160 ? "تعذر إعادة تعيين كلمة المرور. يرجى المحاولة مرة أخرى" : m) : "تعذر إعادة تعيين كلمة المرور");
      setFpStep("reset");
    } finally {
      setFpLoading(false);
    }
  }

  async function onSendOtp() {
    setError(null);
    setInfo(null);
    setShowNoAccount(false);

    // Prefer DOM values (autofill), but never allow empty DOM values to override state.
    const nidFromDom = (nationalIdInputRef.current?.value || "").trim();
    const pwFromDom = (passwordInputRef.current?.value || "").trim();
    const nid = (nidFromDom || nationalId).trim();
    const pw = (pwFromDom || password).trim();

    if (!nid || nid.length < 6) {
      setError("يرجى إدخال رقم هوية صحيح");
      return;
    }
    if (!pw) {
      setError("يرجى إدخال كلمة المرور");
      return;
    }

    let succeeded = false;
    try {
      setLoading(true);
      safetyTimeoutRef.current = window.setTimeout(() => {
        setLoading(false);
        setError("انتهت مهلة الاتصال. يرجى التحقق من الاتصال بالخادم والمحاولة مرة أخرى");
      }, 16000);

      // reCAPTCHA is optional; backend decides whether it is required.
      const res = await initiateEmployeeLoginOtp(nid, pw, captchaToken ?? null);
      setChallengeId(res.challengeId);

      succeeded = true;
      setCaptchaToken(null);
      setStep("otp-sent");
      setCooldown(RESEND_COOLDOWN_S);
      setInfo(
        res.message ||
          (res.maskedMobile ? `تم إرسال رمز التحقق إلى ${res.maskedMobile}` : "تم إرسال رمز التحقق إلى رقم الجوال المرتبط بالحساب")
      );
      setTimeout(() => otpRef.current?.focus(), 60);
    } catch (e: any) {
      console.error("OTP send error:", e);

      const serverCode = e?.response?.data?.code?.toLowerCase?.();
      const serverMessage = e?.response?.data?.message;
      const status = e?.response?.status ?? 0;
      const hasResponse = !!e?.response;

      const serverCodeLower = (serverCode || "").toString().toLowerCase();
      const serverMessageLower = (serverMessage || "").toString().toLowerCase();

      // ✅ Translate backend technical errors to user-friendly Arabic (e.g. SMS provider errors).
      // Do this BEFORE no-account/other fallbacks so the message is not shown raw.
      {
        const translated = translateBackendErrorMessage(
          [serverCodeLower, serverMessageLower, (e?.response?.data?.error ?? "").toString()].filter(Boolean).join(" | ")
        );
        if (translated) {
          if (translated === "ليس لديك حساب في النظام") setShowNoAccount(true);
          setError(translated);
          return;
        }
      }

      // ✅ Invalid credentials: show a clear message and DO NOT show OTP input.
      // Note: server may return 401 or a structured validation response.
      const data = e?.response?.data;
      const codeLower = (serverCodeLower || "").toString();
      const msgLower = (serverMessageLower || "").toString();
      const errorsObj = data?.errors;
      if (errorsObj && typeof errorsObj === "object") {
        const keys = Object.keys(errorsObj).map((k) => (k || "").toLowerCase());
        const idKey = keys.find((k) => k.includes("identifier") || k.includes("nationalid") || k.includes("identificationid"));
        const pwKey = keys.find((k) => k.includes("password") || k === "pass");

        const idVal = idKey ? (errorsObj as any)[idKey] : (errorsObj as any).identifier ?? (errorsObj as any).nationalId;
        const pwVal = pwKey ? (errorsObj as any)[pwKey] : (errorsObj as any).password ?? (errorsObj as any).pass;

        const idHas = !!idKey || (idVal != null && String(idVal).trim() !== "");
        const pwHas = !!pwKey || (pwVal != null && String(pwVal).trim() !== "");

        if (idHas && !pwHas) {
          setError("رقم الهوية غير صحيح");
          setShowNoAccount(false);
          return;
        }
        if (pwHas && !idHas) {
          setError("كلمة المرور غير صحيحة");
          setShowNoAccount(false);
          return;
        }
        if (idHas || pwHas) {
          setError("رقم الهوية أو كلمة المرور غير صحيحة");
          setShowNoAccount(false);
          return;
        }
      }

      if (
        status === 401 ||
        codeLower.includes("invalid_credentials") ||
        codeLower.includes("bad_credentials") ||
        codeLower.includes("wrong_password") ||
        msgLower.includes("invalid credentials") ||
        msgLower.includes("bad credentials") ||
        msgLower.includes("wrong password") ||
        msgLower.includes("unauthorized") ||
        msgLower.includes("invalid password") ||
        msgLower.includes("كلمة المرور غير صحيحة") ||
        msgLower.includes("بيانات الدخول غير") ||
        msgLower.includes("اسم المستخدم أو كلمة المرور")
      ) {
        setError("رقم الهوية أو كلمة المرور غير صحيحة");
        setShowNoAccount(false);
        return;
      }

      if (serverCode === "user_not_found" || isNoAccountError(e)) {
        setShowNoAccount(true);
        setError("ليس لديك حساب في النظام");
      } else {
        const msg = (e?.message || "").toLowerCase();

        if (msg === "request_timeout" || msg.includes("timeout")) {
          setError("انتهت مهلة الاتصال. يرجى التحقق من الاتصال بالخادم والمحاولة مرة أخرى");
        } else if (!hasResponse && (msg === "network_error" || msg.includes("network"))) {
          setError("فشل الاتصال بالخادم. يرجى التحقق من الاتصال والمحاولة مرة أخرى");
        } else if (msg.includes("rate_limited") || msg.includes("cooldown")) {
          setError("فشل إرسال رمز التحقق. يرجى المحاولة مرة أخرى");
        } else if (msg.includes("captcha") || msg.includes("recaptcha")) {
          setError("يرجى التحقق من reCAPTCHA ثم المحاولة مرة أخرى");
        } else if (msg.includes("invalid_email")) {
          setError("يرجى إدخال رقم هوية صحيح");
        } else if (!hasResponse && (msg.includes("fetch") || msg.includes("failed"))) {
          setError("فشل الاتصال بالخادم. يرجى التحقق من الاتصال والمحاولة مرة أخرى");
        } else {
          const errorMsg =
            serverMessage ||
            e?.message ||
            e?.toString() ||
            "فشل إرسال رمز التحقق";

          const translated = translateBackendErrorMessage(errorMsg);
          const finalMsg = translated || (errorMsg.length > 140 ? "فشل إرسال رمز التحقق. يرجى المحاولة مرة أخرى" : errorMsg);
          setError(finalMsg);
        }
      }

    } finally {
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }
      setLoading(false);
      if (!succeeded) {
        recaptchaRef.current?.reset();
        setCaptchaToken(null);
      }
    }
  }

  async function onVerify() {
    setError(null);
    setInfo(null);
    setShowNoAccount(false);

    const nidFromDom = (nationalIdInputRef.current?.value || "").trim();
    const pwFromDom = (passwordInputRef.current?.value || "").trim();
    const nid = (nidFromDom || nationalId).trim();
    const pw = (pwFromDom || password).trim();
    const ch = (challengeId || "").trim();

    if (!nid || nid.length < 6) {
      setError("يرجى إدخال رقم هوية صحيح");
      return;
    }
    if (!pw) {
      setError("يرجى إدخال كلمة المرور");
      return;
    }
    if (!ch) {
      setError("تعذر متابعة التحقق. يرجى إعادة إرسال رمز التحقق.");
      return;
    }

    const trimmed = code.trim();
    if (!trimmed || !/^\d{1,6}$/.test(trimmed)) {
      setError("يرجى إدخال رمز التحقق");
      return;
    }

    try {
      setLoading(true);
      setStep("verifying");

      const res = await verifyEmployeeLoginOtp(ch, trimmed, pw);
      if (!res?.token) throw new Error("Invalid response from server");

      // ✅ Benefaction auth uses authToken (do NOT switch to employee token key)
      setAuthToken(res.token);

      if (res.user) {
        localStorage.setItem("authUser", JSON.stringify(res.user));
      }

      // Prefer server-resolved continueResult (authoritative)
      // (employee verify may not include continueResult, so we fallback to URL token)
      let continuePath = "";

      // If server didn't provide path, try to resolve from continueToken in URL
      if (!continuePath && continueToken) {
        const resolved = resolveContinueToken(continueToken);
        continuePath = resolved?.path || "";
      }

      const sanitized = sanitizeInternalPath(continuePath);
      const nextPath = sanitized || returnUrlSafe || "/my-dashboard";

      // Use state from server when present; otherwise decode from continue token
      let nextState: any = undefined;
      if (continueToken) {
        const resolved = resolveContinueToken(continueToken);
        nextState = resolved?.state;
      }

      if (hasNavigatedRef.current) return;
      hasNavigatedRef.current = true;

      navigate(nextPath, { replace: true, state: nextState });
    } catch (e: any) {
  console.error("Donation login error:", e);

  // ---- helper: extract message from .NET / Odoo payloads (even if stringified JSON) ----
  const extractServerMessage = (err: any): string => {
    let data = err?.response?.data;

    // Some backends send JSON as a string
    if (typeof data === "string") {
      const s = data.trim();
      if ((s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"))) {
        try { data = JSON.parse(s); } catch { /* ignore */ }
      }
    }

    const msg =
      // common
      data?.message ??
      data?.Message ??
      // your JSON-RPC-ish envelope
      data?.result?.body?.message ??
      data?.result?.body?.Message ??
      data?.body?.message ??
      data?.body?.Message ??
      // fallback
      err?.message ??
      err?.toString?.() ??
      "";

    return typeof msg === "string" ? msg : String(msg ?? "");
  };

  const serverMsg = extractServerMessage(e);
  const serverMsgLower = serverMsg.toLowerCase();

  const status = e?.response?.status ?? e?.status ?? null;
  const clientMsgLower = (e?.message || "").toLowerCase();

  // ✅ 1) "user not found" MUST show "no account" (your intentional test case)
  if (
    serverMsgLower.includes("user not found") ||
    serverMsgLower.includes("not registered") ||
    serverMsgLower.includes("not_registered") ||
    serverMsgLower.includes("email_not_found") ||
    serverMsgLower.includes("email_not_registered") ||
    serverMsgLower.includes("phone_not_found") ||
    serverMsgLower.includes("phone_not_registered")
  ) {
    setShowNoAccount(true);
    setError("ليس لديك حساب في النظام");
    setStep("otp-sent");
    return;
  }

  // ✅ 2) If backend returns 401 -> show Unauthorized (NOT OTP incorrect)
  if (status === 401 || clientMsgLower === "auth_401" || clientMsgLower.includes("401")) {
    setShowNoAccount(false);
    // In this flow, 401 during OTP verify almost always means the password is wrong
    // (initiate-otp may still issue a challengeId). Return user to credentials step.
    setError("كلمة المرور غير صحيحة");
    setStep("credentials");
    setChallengeId(null);
    setCode("");
    return;
  }

  // ✅ 3) Keep your existing "no account" detector (but after server message check)
  if (isNoAccountError(e)) {
    setShowNoAccount(true);
    setError("ليس لديك حساب في النظام");
    setStep("otp-sent");
    return;
  }

  // ✅ 4) Network / timeout
  if (clientMsgLower.includes("network") || clientMsgLower.includes("fetch") || clientMsgLower.includes("failed")) {
    setShowNoAccount(false);
    setError("فشل الاتصال بالخادم. يرجى التحقق من الاتصال والمحاولة مرة أخرى");
    setStep("otp-sent");
    return;
  }

  // ✅ 5) OTP incorrect ONLY when message indicates invalid/incorrect OTP
  if (
    serverMsgLower.includes("invalid otp") ||
    serverMsgLower.includes("incorrect otp") ||
    (serverMsgLower.includes("otp") && serverMsgLower.includes("invalid")) ||
    clientMsgLower.includes("invalid") ||
    clientMsgLower.includes("incorrect")
  ) {
    setShowNoAccount(false);
    setError("رمز التحقق غير صحيح. يرجى المحاولة مرة أخرى");
    setStep("otp-sent");
    return;
  }

  // ✅ 6) Final fallback: show backend message if any, else generic
  const fallback = serverMsg || "حدث خطأ أثناء التحقق. يرجى المحاولة مرة أخرى";
  setShowNoAccount(false);
  const translatedFallback = translateBackendErrorMessage(fallback);
  setError(translatedFallback || (fallback.length > 140 ? "حدث خطأ أثناء التحقق. يرجى المحاولة مرة أخرى" : fallback));
  setStep("otp-sent");
} finally {
  setLoading(false);
}


  }

  // Keep resend/verify gating strict; allow "send" click even if autofill didn't update state yet.
  const canSend = !loading && step === "credentials" && cooldown === 0;
  const canResend = step !== "credentials" && !loading && cooldown === 0;
  const canVerify = step !== "credentials" && !loading && /^\d{1,6}$/.test(code.trim());

  const fpPwStrength = useMemo(() => getPwStrength(fpNewPassword), [fpNewPassword]);
  const fpPwUnsafe = fpPwStrength.level === "weak";

  const fpCanStart =
    !!fpNationalId.trim() &&
    fpNationalId.trim().length >= 6 &&
    /^\d{4}$/.test(fpLast4.trim()) &&
    !fpLoading &&
    fpStep === "start";
  const fpCanResend = !fpLoading && fpStep === "otp" && fpCooldown === 0;
  const fpCanVerify = !fpLoading && fpStep === "otp" && /^\d{1,6}$/.test(fpOtp.trim());
  const fpCanReset =
    !fpLoading &&
    fpStep === "reset" &&
    !!fpNewPassword.trim() &&
    !fpPwUnsafe &&
    fpNewPassword.trim() === fpConfirmPassword.trim();

  const numberSar = useCallback(
    (v: number | null | undefined) => (typeof v === "number" && !Number.isNaN(v) ? v.toLocaleString("en-US") : "-"),
    []
  );

  const landPrice = useMemo(() => {
    if (!landPreview) return 0;
    const previewAny = landPreview as any;
    const p =
      landPreview.aproxLandPrice ??
      previewAny?.approxLandPrice ??
      previewAny?.aprox_land_price;
    return typeof p === "number" ? p : 0;
  }, [landPreview]);

  const landLabel = useMemo(() => {
    if (!landPreview) return "";
    const previewAny = landPreview as any;
    return previewAny?.code || previewAny?.landCode || landPreview.sequence || `#${landPreview.id}`;
  }, [landPreview]);

  const fullAddr = useMemo(() => {
    if (!landPreview) return "";
    return (
      landPreview.fullAddress ||
      [landPreview.regionName, landPreview.cityName, (landPreview as any).centerName, landPreview.districtName]
        .filter(Boolean)
        .join(" / ")
    );
  }, [landPreview]);

  const mapEmbedSrc = useMemo(() => {
    if (!landPreview?.latitude || !landPreview?.longitude) return "";
    return `https://www.google.com/maps?q=${landPreview.latitude},${landPreview.longitude}&hl=ar&z=16&output=embed`;
  }, [landPreview]);

  const mapsDeeplink = useMemo(() => {
    if (!landPreview?.latitude || !landPreview?.longitude) return "";
    return `https://maps.google.com/?q=${landPreview.latitude},${landPreview.longitude}&hl=ar`;
  }, [landPreview]);

  const qrImgSrc = useMemo(() => {
    if (!mapsDeeplink) return "";
    return `https://quickchart.io/qr?text=${encodeURIComponent(mapsDeeplink)}&margin=2&size=160`;
  }, [mapsDeeplink]);

  const landInfoRows = useMemo(
    () =>
      landPreview
        ? buildLandInfoRows(landPreview as unknown as Record<string, unknown>, landLabel, fullAddr)
        : null,
    [landPreview, landLabel, fullAddr]
  );

  const landPanelT = useCallback((_key: string, fallback?: any) => (typeof fallback === "string" ? fallback : ""), []);

  return (
    <main dir={dir} className="min-h-screen bg-gray-100">
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6" />
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
        <div className="max-w-xl mx-auto">
          {/* بطاقة الدخول برقم الهوية + كلمة المرور + رمز التحقق */}
          <article className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8 flex flex-col h-full">
            {view === "login" && (
              <>
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-sa-700">تسجيل الدخول</h2>
              <p className="text-gray-600 mt-2">أدخل رقم الهوية وكلمة المرور وسيتم إرسال رمز تحقق إلى رقم الجوال المرتبط بالحساب.</p>
            </div>

            <div className="space-y-5 w-full flex-1 flex flex-col">
              {/* CREDENTIALS CARD */}
              <section className="rounded-2xl border border-neutral-200 bg-neutral-50/60 flex-shrink-0">
                <div className="p-4 sm:p-6">
                  <div className="mb-4 text-center">
                    <div className="text-sm font-semibold text-neutral-700">بيانات الدخول</div>
                  </div>

                    <div className="space-y-4">
                    <div className="space-y-1">
                      <label dir="rtl" className="block text-xs font-medium text-neutral-600 text-right">رقم الهوية</label>
                      <input
                        ref={nationalIdInputRef}
                        type="text"
                        dir="ltr"
                        inputMode="numeric"
                        className="w-full h-12 rounded-xl border border-neutral-300 bg-white px-3 text-base outline-none ring-0 focus:border-sa-500 focus:ring-2 focus:ring-sa-200 text-left placeholder:text-right"
                        placeholder="رقم الهوية"
                        value={nationalId}
                        onChange={(e) => {
                          setNationalId(e.target.value.replace(/\D/g, "").slice(0, 20));
                          setShowNoAccount(false);
                        }}
                        onInput={(e) => {
                          const v = (e.currentTarget.value || "").replace(/\D/g, "").slice(0, 20);
                          if (v !== nationalId) setNationalId(v);
                        }}
                        disabled={loading && step !== "otp-sent"}
                      />
                    </div>

                    <div className="space-y-1">
                      <label dir="rtl" className="block text-xs font-medium text-neutral-600 text-right">كلمة المرور</label>
                      <input
                        ref={passwordInputRef}
                        type="password"
                        dir="ltr"
                        className="w-full h-12 rounded-xl border border-neutral-300 bg-white px-3 text-base outline-none ring-0 focus:border-sa-500 focus:ring-2 focus:ring-sa-200 text-left placeholder:text-right"
                        placeholder="كلمة المرور"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setShowNoAccount(false);
                        }}
                        onInput={(e) => {
                          const v = e.currentTarget.value || "";
                          if (v !== password) setPassword(v);
                        }}
                        disabled={loading && step !== "otp-sent"}
                      />
                    </div>

{/* reCAPTCHA */}
                  <div className="mt-4 flex justify-center">
                    {RECAPTCHA_SITE_KEY ? (
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={(tok) => setCaptchaToken(tok)}
                        hl="ar"
                      />
                    ) : (
                      <div
                        role="alert"
                        className="rounded-xl border border-amber-200 bg-amber-50 text-amber-800 px-4 py-2 text-sm"
                      >
                        reCAPTCHA site key missing. Set{" "}
                        <code className="font-mono">VITE_RECAPTCHA_SITE_KEY</code> أو{" "}
                        <code className="font-mono">REACT_APP_RECAPTCHA_SITE_KEY</code>.
                      </div>
                    )}
                  </div>



                    <button
                      type="button"
                      onClick={onSendOtp}
                      disabled={!canSend}
                      className="w-full h-12 px-5 rounded-xl font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-sa-600 hover:bg-sa-700"
                    >
                      {loading && step === "credentials" ? "جاري الإرسال..." : "إرسال رمز التحقق"}
                    </button>

                    <div className="flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setView("forgot");
                          setFpError(null);
                          setFpInfo(null);
                          setFpStep("start");
                          setFpChallengeId(null);
                          setFpOtp("");
                          setFpNewPassword("");
                          setFpConfirmPassword("");
                          setFpCooldown(0);
                        }}
                        className="text-sm font-semibold text-sa-700 hover:text-sa-800 underline underline-offset-4"
                      >
                        نسيت كلمة المرور؟
                      </button>

                      <Link
                        to={signUpPath}
                        className="text-sm font-semibold text-sa-700 hover:text-sa-800 underline underline-offset-4"
                      >
                        ليس لديك حساب؟ سجل الآن
                      </Link>
                    </div>
                  </div>

                  
                </div>
              </section>

              {/* OTP CARD */}
              {step !== "credentials" && (
                <section className="rounded-2xl border border-neutral-200 bg-neutral-50/60">
                  <div className="p-4 sm:p-6">
                    <div className="mb-4 text-center">
                      <div className="text-sm font-semibold text-neutral-700">رمز التحقق</div>
                      <div className="text-xs text-neutral-500 mt-1">أدخل الرمز المرسل إلى رقم جوالك</div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <input
                          ref={otpRef}
                          dir="ltr"
                          inputMode="numeric"
                          maxLength={6}
                          className="w-full h-12 rounded-xl border border-neutral-300 bg-white px-3 text-base tracking-widest text-center outline-none focus:border-sa-500 focus:ring-2 focus:ring-sa-200"
                          placeholder="••••••"
                          value={code}
                          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          disabled={loading && step === "verifying"}
                        />
                        <div className="text-neutral-400 text-xs mt-1 text-center">
                          {cooldown > 0
                            ? `إعادة الإرسال بعد ${cooldown} ثانية`
                            : "أدخل رمز التحقق المرسل إلى رقم جوالك (من 1 إلى 6 أرقام)"}
                        </div>
                      </div>

                      <div className="flex items-stretch gap-2">
                        <button
                          type="button"
                          onClick={onSendOtp}
                          disabled={!canResend}
                          className="flex-1 h-12 px-4 rounded-xl border border-neutral-300 text-neutral-700 hover:bg-neutral-50 transition focus:outline-none focus:ring-2 focus:ring-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={cooldown > 0 ? `إعادة الإرسال بعد ${cooldown} ثانية` : undefined}
                        >
                          إعادة الإرسال
                        </button>
                        <button
                          type="button"
                          onClick={onVerify}
                          disabled={!canVerify}
                          className="flex-1 h-12 px-5 rounded-xl font-semibold text-white bg-sa-600 hover:bg-sa-700 transition focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {step === "verifying" ? "جاري التحقق..." : "تحقق"}
                        </button>
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* messages */}
              {error && (
                <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-800 text-sm">
                  {error}
                  {showNoAccount && (
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(continueToken ? `/resident-signup?c=${encodeURIComponent(continueToken)}` : "/resident-signup")
                        }
                        className="inline-flex h-11 items-center justify-center rounded-xl bg-sa-600 px-5 text-white text-sm font-semibold hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2"
                      >
                        إنشاء حساب
                      </button>
                    </div>
                  )}
                </div>
              )}

              {info && !error && (
                <div role="alert" className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-green-800 text-sm">
                  {info}
                </div>
              )}
            </div>
              </>
            )}

            {view === "forgot" && (
              <>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-sa-700">إعادة تعيين كلمة المرور</h2>
                  <p className="text-gray-600 mt-2">
                    أدخل رقم الهوية وآخر 4 أرقام من جوالك، ثم أدخل رمز التحقق لإعادة تعيين كلمة المرور.
                  </p>
                </div>

                <div className="space-y-5 w-full flex-1 flex flex-col">
                  {fpStep === "start" && (
                    <section className="bg-white border border-neutral-200 rounded-2xl shadow-sm flex-shrink-0">
                      <div className="p-4 sm:p-6 space-y-3">
                        <label className="block text-neutral-500 text-sm mb-2 text-center">بيانات التحقق</label>
                        <input
                          type="text"
                          dir="ltr"
                          inputMode="numeric"
                          className="w-full h-12 rounded-xl border border-neutral-300 bg-white px-3 text-base outline-none ring-0 focus:border-sa-500 focus:ring-2 focus:ring-sa-200 text-left placeholder:text-right"
                          placeholder="رقم الهوية"
                          value={fpNationalId}
                          onChange={(e) => setFpNationalId(e.target.value.replace(/\D/g, "").slice(0, 20))}
                          disabled={fpLoading}
                        />
                        <input
                          type="text"
                          dir="ltr"
                          inputMode="numeric"
                          maxLength={4}
                          className="w-full h-12 rounded-xl border border-neutral-300 bg-white px-3 text-base outline-none ring-0 focus:border-sa-500 focus:ring-2 focus:ring-sa-200 text-left placeholder:text-right"
                          placeholder="آخر 4 أرقام من رقم الجوال"
                          value={fpLast4}
                          onChange={(e) => setFpLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
                          disabled={fpLoading}
                        />

                        <div className="mt-3 flex justify-center">
                          {RECAPTCHA_SITE_KEY ? (
                            <ReCAPTCHA
                              ref={forgotRecaptchaRef}
                              sitekey={RECAPTCHA_SITE_KEY}
                              onChange={(tok) => setFpCaptchaToken(tok)}
                              hl="ar"
                            />
                          ) : null}
                        </div>

                        <button
                          type="button"
                          onClick={forgotStart}
                          disabled={!fpCanStart}
                          className="w-full h-12 px-5 rounded-xl font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-sa-600 hover:bg-sa-700"
                        >
                          {fpLoading ? "جاري الإرسال..." : "إرسال رمز التحقق"}
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setView("login");
                            setFpError(null);
                            setFpInfo(null);
                            setFpStep("start");
                            setFpChallengeId(null);
                            setFpOtp("");
                            setFpNewPassword("");
                            setFpConfirmPassword("");
                            setFpCooldown(0);
                          }}
                          className="w-full text-center text-sm font-semibold text-neutral-700 hover:text-neutral-900 underline underline-offset-4"
                        >
                          رجوع لتسجيل الدخول
                        </button>
                      </div>
                    </section>
                  )}

                  {fpStep === "otp" && (
                    <section className="bg-white border border-neutral-200 rounded-2xl shadow-sm">
                      <div className="p-4 sm:p-6">
                        <label className="block text-neutral-500 text-sm mb-2 text-center">رمز التحقق</label>
                        <div className="space-y-3">
                          <div>
                            <input
                              ref={otpRef}
                              dir="ltr"
                              inputMode="numeric"
                              maxLength={6}
                              className="w-full h-12 rounded-xl border border-neutral-300 bg-white px-3 text-base tracking-widest text-center outline-none focus:border-sa-500 focus:ring-2 focus:ring-sa-200"
                              placeholder="••••••"
                              value={fpOtp}
                              onChange={(e) => setFpOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                              disabled={fpLoading}
                            />
                            <div className="text-neutral-400 text-xs mt-1 text-center">
                              {fpCooldown > 0 ? `إعادة الإرسال بعد ${fpCooldown} ثانية` : "أدخل رمز التحقق المرسل إلى رقم جوالك"}
                            </div>
                          </div>

                          <div className="flex items-stretch gap-2">
                            <button
                              type="button"
                              onClick={forgotStart}
                              disabled={!fpCanResend}
                              className="flex-1 h-12 px-4 rounded-xl border border-neutral-300 text-neutral-700 hover:bg-neutral-50 transition focus:outline-none focus:ring-2 focus:ring-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              إعادة الإرسال
                            </button>
                            <button
                              type="button"
                              onClick={forgotVerify}
                              disabled={!fpCanVerify}
                              className="flex-1 h-12 px-5 rounded-xl font-semibold text-white bg-sa-600 hover:bg-sa-700 transition focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {fpLoading ? "جاري التحقق..." : "تحقق"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </section>
                  )}

                  {(fpStep === "reset" || fpStep === "resetting") && (
                    <section className="bg-white border border-neutral-200 rounded-2xl shadow-sm">
                      <div className="p-4 sm:p-6 space-y-3">
                        <label className="block text-neutral-500 text-sm mb-2 text-center">تعيين كلمة المرور</label>
                        {fpNewPassword.trim() ? (
                          <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-neutral-700">مدى أمان كلمة المرور</span>
                              <span
                                className={
                                  fpPwStrength.level === "weak"
                                    ? "text-red-700"
                                    : fpPwStrength.level === "medium"
                                      ? "text-amber-700"
                                      : "text-green-700"
                                }
                              >
                                {fpPwStrength.label}
                              </span>
                            </div>
                            <div className="mt-2 h-2 w-full rounded bg-neutral-200 overflow-hidden">
                              <div
                                className={
                                  "h-2 rounded " +
                                  (fpPwStrength.level === "weak"
                                    ? "bg-red-500"
                                    : fpPwStrength.level === "medium"
                                      ? "bg-amber-500"
                                      : "bg-green-500")
                                }
                                style={{ width: `${Math.min(100, (fpPwStrength.score / 5) * 100)}%` }}
                              />
                            </div>
                            {fpPwStrength.level === "weak" ? (
                              <div className="mt-2 text-xs text-red-700">
                                كلمة المرور غير آمنة. اختر كلمة مرور أقوى (8 أحرف على الأقل وتحتوي على حروف وأرقام).
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        <input
                          type="password"
                          dir="rtl"
                          className="w-full h-12 rounded-xl border border-neutral-300 bg-white px-3 text-base outline-none ring-0 focus:border-sa-500 focus:ring-2 focus:ring-sa-200 text-right"
                          placeholder="كلمة المرور الجديدة"
                          value={fpNewPassword}
                          onChange={(e) => setFpNewPassword(e.target.value)}
                          disabled={fpLoading}
                        />
                        <input
                          type="password"
                          dir="rtl"
                          className="w-full h-12 rounded-xl border border-neutral-300 bg-white px-3 text-base outline-none ring-0 focus:border-sa-500 focus:ring-2 focus:ring-sa-200 text-right"
                          placeholder="تأكيد كلمة المرور"
                          value={fpConfirmPassword}
                          onChange={(e) => setFpConfirmPassword(e.target.value)}
                          disabled={fpLoading}
                        />

                        {fpNewPassword && fpConfirmPassword && fpNewPassword !== fpConfirmPassword ? (
                          <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-sm">
                            كلمة المرور غير مطابقة
                          </div>
                        ) : null}

                        <button
                          type="button"
                          onClick={forgotReset}
                          disabled={!fpCanReset}
                          className="w-full h-12 px-5 rounded-xl font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-sa-600 hover:bg-sa-700"
                        >
                          {fpStep === "resetting" || fpLoading ? "جاري إعادة التعيين..." : "إعادة تعيين"}
                        </button>
                      </div>
                    </section>
                  )}

                  {fpError && (
                    <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-800 text-sm">
                      {fpError}
                    </div>
                  )}
                  {fpInfo && !fpError && (
                    <div role="alert" className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-green-800 text-sm">
                      {fpInfo}
                    </div>
                  )}
                </div>
              </>
            )}
          </article>
        </div>

        {landIdFromContinue && (
          <aside className="mt-8 space-y-5">
            <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm p-4 text-sm text-neutral-600">
              <div className="font-semibold text-neutral-900 mb-1">تفاصيل الأرض المختارة</div>
              <p>تم حفظ بيانات الأرض في الصفحة لتتمكن من مراجعتها قبل إكمال تسجيل الدخول.</p>
            </div>

            {landPreviewLoading && (
              <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm p-4 text-sm text-neutral-600">
                جاري تحميل بيانات الأرض…
              </div>
            )}

            {landPreviewError && !landPreviewLoading && (
              <div className="rounded-2xl border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">
                {landPreviewError}
              </div>
            )}

            {landPreview && !landPreviewLoading && landInfoRows && (
              <LandInfoPanel
                t={landPanelT}
                mapEmbedSrc={mapEmbedSrc}
                qrImgSrc={qrImgSrc}
                mapsDeeplink={mapsDeeplink}
                landLabel={landLabel}
                landInfo={landInfoRows}
                landPrice={landPrice}
                numberSar={numberSar}
              />
            )}
          </aside>
        )}
      </div>
    </main>
  );
}